import { FieldCacheService, NSARouterMakerResult } from 'namirasoft-account-react';
import { EncodingOperation } from 'namirasoft-core';
import { CategoryRow, CategoryScopeRow } from 'namirasoft-field';
import { UserFullRow } from 'namirasoft-password';
import { NamirasoftMap } from 'namirasoft-site-map';
import { EncryptionOperation, NSBarAlert, NSBox, NSBoxCombo, NSButton, NSButtonBlue, NSPanel, NSRow, NSTitle } from 'namirasoft-site-react';
import { Component, createRef } from 'react';
import { HashOperation } from '../HashOperation';
import { ImportedRow } from '../pages/ImportPage';
import ButtonStyles from '../pages/SetupWizard.module.css';
import Styles from '../components/ImportColumnSettingsStep.module.css';
import { PasswordRouterProps } from '../PasswordRouterProps';
import { Storage } from '../Storage';

type CategorySource = 'Namirasoft' | 'CSV';

export interface ImportColumnSettingsStepProps extends NSARouterMakerResult, PasswordRouterProps
{
    user: UserFullRow;
    csvHeaders: string[];
    csvBody: string[][];
    onComplete: (importedRows: ImportedRow[]) => void;
    onBack: () => void;
}

interface ImportColumnSettingsStepState
{
    category_scopes: CategoryScopeRow[];
    categories: CategoryRow[];
    category_source?: CategorySource;
    missing_category_names: string[];
}

class ImportColumnSettingsStep extends Component<ImportColumnSettingsStepProps, ImportColumnSettingsStepState>
{
    NSBoxCombo_NameColumn = createRef<NSBoxCombo>();
    NSBoxCombo_UsernameColumn = createRef<NSBoxCombo>();
    NSBoxCombo_PasswordColumn = createRef<NSBoxCombo>();
    NSBoxCombo_URLColumn = createRef<NSBoxCombo>();
    NSBoxCombo_DescriptionColumn = createRef<NSBoxCombo>();
    NSBoxCombo_CategoryColumn = createRef<NSBoxCombo>();
    NSBoxCombo_CategoryID = createRef<NSBoxCombo>();
    NSBoxCombo_CategoryScopeID = createRef<NSBoxCombo>();

    constructor(props: ImportColumnSettingsStepProps)
    {
        super(props);
        this.state = {
            category_scopes: [],
            categories: [],
            missing_category_names: [],
        };
        this.createImportedRows = this.createImportedRows.bind(this);
        this.onCategoryColumnChanged = this.onCategoryColumnChanged.bind(this);
    }

    override componentDidMount()
    {
        this.loadCategories();
    }

    override componentDidUpdate(prevProps: ImportColumnSettingsStepProps)
    {
        if (prevProps.csvHeaders !== this.props.csvHeaders)
        {
            this.NSBoxCombo_NameColumn.current?.reload();
            this.NSBoxCombo_UsernameColumn.current?.reload();
            this.NSBoxCombo_PasswordColumn.current?.reload();
            this.NSBoxCombo_URLColumn.current?.reload();
            this.NSBoxCombo_DescriptionColumn.current?.reload();
            this.NSBoxCombo_CategoryColumn.current?.reload();
            this.onCategoryColumnChanged();
        }
    }

    getMissingCategoryNames(category_ci: number, categories: CategoryRow[]): string[]
    {
        if (category_ci < 0)
            return [];
        let existing_names = new Set(categories.map(category => category.name));
        let csv_names = new Set(this.props.csvBody.map(row => String(row[category_ci] ?? '').trim()).filter(Boolean));
        return [...csv_names].filter(name => !existing_names.has(name)).sort((a, b) => a.localeCompare(b));
    }

    onCategoryColumnChanged()
    {
        let category_ci = Number(this.NSBoxCombo_CategoryColumn.current?.getValueOne() ?? -1);
        this.setState((prev) => ({ missing_category_names: this.getMissingCategoryNames(category_ci, prev.categories) }));
    }

    getMissingCategoryNamesPreview(): string
    {
        let names = this.state.missing_category_names;
        let preview = names.slice(0, 5).join(', ');
        return names.length > 5 ? `${preview} and ${names.length - 5} more` : preview;
    }

    getFieldCache()
    {
        return FieldCacheService.get(
            this.props.account.token_manager,
            this.props.product_id,
            this.props.notifier.onError,
        );
    }

    async loadCategories()
    {
        let rows = await this.getFieldCache().get();
        this.setState(
            () => ({
                category_scopes: [...rows.category_scopes].sort((a, b) => a.name.localeCompare(b.name)),
                categories: [...rows.categories].sort((a, b) => a.name.localeCompare(b.name)),
            }),
            () =>
            {
                this.NSBoxCombo_CategoryID.current?.reload();
                this.NSBoxCombo_CategoryScopeID.current?.reload();
                this.onCategoryColumnChanged();
            });
    }

    async createImportedRows(onFinished: () => void)
    {
        let name_ci: number;
        let username_ci: number;
        let password_ci: number;
        let url_ci: number;
        let description_ci: number;
        let category_ci: number;
        try
        {
            name_ci = Number(this.NSBoxCombo_NameColumn.current?.getValueOne() ?? -1);
            username_ci = Number(this.NSBoxCombo_UsernameColumn.current?.getValueOne() ?? -1);
            password_ci = Number(this.NSBoxCombo_PasswordColumn.current?.getValueOne() ?? -1);
            url_ci = Number(this.NSBoxCombo_URLColumn.current?.getValueOne() ?? -1);
            description_ci = Number(this.NSBoxCombo_DescriptionColumn.current?.getValueOne() ?? -1);
            category_ci = this.state.category_source === 'CSV'
                ? Number(this.NSBoxCombo_CategoryColumn.current?.getValueOne() ?? -1)
                : -1;

            if (this.state.category_source === 'CSV' && category_ci === -1)
                throw new Error('Please select a category column');

            let columns_cis = [name_ci, username_ci, password_ci, url_ci, description_ci, category_ci].filter(ci => ci > -1);
            let is_duplicated = new Set(columns_cis).size < columns_cis.length;
            if (is_duplicated)
                throw new Error('Please do not select duplicate columns');
        }
        catch (error)
        {
            // Input validation errors are not reported by the server, so notify here.
            this.props.notifier.onError(error as string);
            onFinished();
            return;
        }

        let existing_hashes: string[];
        try
        {
            let existing_entries = (await this.props.servers.password.entry.List([], 1, 1000, [], null)).rows;
            existing_hashes = existing_entries.map(e => e.hash);
        }
        catch
        {
            // The server already reported the error through the notifier.
            onFinished();
            return;
        }

        let imported_rows: ImportedRow[] = [];
        try
        {
            let master_key = await Storage.getMasterKey(this.props.user.id);

            for (let i = 0; i < this.props.csvBody.length; i++)
            {
                let row = this.props.csvBody[i];

                let encoded_password = EncodingOperation.Base64Encode(row[password_ci].toString());
                let encrypted_password = await EncryptionOperation.AES256GCMEncrypt(master_key, encoded_password);

                let url = row[url_ci];
                let username = row[username_ci];
                let hash = await HashOperation.hashEntry(String(username), String(url));

                let imported = existing_hashes.includes(hash);

                imported_rows.push({
                    id: i + 1,
                    name: String(row[name_ci]),
                    username: String(row[username_ci]),
                    password: encrypted_password,
                    url: String(row[url_ci]),
                    description: description_ci > -1 ? String(row[description_ci]) : undefined,
                    category_name: category_ci > -1 ? String(row[category_ci] ?? '').trim() || undefined : undefined,
                    hash,
                    imported,
                });
            }
        }
        catch (error)
        {
            // Local encryption/parsing errors are not reported by the server, so notify here.
            this.props.notifier.onError(error as string);
            onFinished();
            return;
        }

        if (this.state.category_source === 'CSV' && category_ci > -1)
        {
            try
            {
                let category_map = new Map(this.state.categories.map(category => [category.name, category.id]));
                let imported_category_names = new Set(imported_rows.map(row => row.category_name).filter(Boolean) as string[]);
                let missing_category_names = [...imported_category_names].filter(name => !category_map.has(name));

                let category_scope_id = String(this.NSBoxCombo_CategoryScopeID.current?.getValueOne() ?? '');
                if (missing_category_names.length > 0 && category_scope_id)
                {
                    let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
                    let field_server = namirasoft.field.getServer();

                    for (const category_name of missing_category_names)
                    {
                        let new_category = await field_server.category.Create({
                            workspace_id: "",
                            category_scope_id,
                            name: category_name,
                            description: null,
                            category_category: [],
                            category_field: [],
                            category_tag: [],
                        });
                        category_map.set(new_category.name, new_category.id);
                    }

                    this.getFieldCache().del();
                    this.loadCategories();
                }

                imported_rows.forEach(row =>
                {
                    if (row.category_name)
                        row.category_id = category_map.get(row.category_name);
                });
            }
            catch (error)
            {
                // The field server reports its errors to the console, so notify here.
                this.props.notifier.onError(error as string);
                onFinished();
                return;
            }
        }
        else if (this.state.category_source === 'Namirasoft')
        {
            try
            {
                let category_id = String(this.NSBoxCombo_CategoryID.current?.getValueOne() ?? '');
                if (!category_id)
                    throw new Error('Please select a category');

                let category = this.state.categories.find(category => category.id === category_id);
                imported_rows.forEach(row =>
                {
                    row.category_id = category_id;
                    row.category_name = category?.name;
                });
            }
            catch (error)
            {
                // Input validation errors are not reported by the server, so notify here.
                this.props.notifier.onError(error as string);
                onFinished();
                return;
            }
        }

        this.props.onComplete(imported_rows);
        onFinished();
    }

    override render()
    {
        let csv_headers = this.props.csvHeaders.map((header, index) => ({ title: header, value: String(index) }));

        return (
            <NSPanel classList={["mx-auto"]}>
                <NSTitle title="Select Columns" style={{ fontSize: '1.25rem' }} />
                <NSRow>
                    <NSBoxCombo
                        ref={this.NSBoxCombo_NameColumn}
                        title="Name"
                        required
                        multiple={false}
                        getOptions={() => csv_headers}
                    />
                    <NSBoxCombo
                        ref={this.NSBoxCombo_UsernameColumn}
                        title="Username"
                        required
                        multiple={false}
                        getOptions={() => csv_headers}
                    />
                </NSRow>
                <NSRow>
                    <NSBoxCombo
                        ref={this.NSBoxCombo_PasswordColumn}
                        title="Password"
                        required
                        multiple={false}
                        getOptions={() => csv_headers}
                    />
                    <NSBoxCombo
                        ref={this.NSBoxCombo_URLColumn}
                        title="URL"
                        required
                        multiple={false}
                        getOptions={() => csv_headers}
                    />
                </NSRow>
                <NSRow style={{ justifyContent: 'flex-start' }}>
                    <NSBoxCombo
                        ref={this.NSBoxCombo_DescriptionColumn}
                        title="Description"
                        required={false}
                        multiple={false}
                        getOptions={() => csv_headers}
                    />
                    <NSBoxCombo
                        title="Category Source"
                        required={false}
                        multiple={false}
                        getOptions={() => [
                            { title: 'Namirasoft Password', value: 'Namirasoft' },
                            { title: 'CSV Column', value: 'CSV' },
                        ]}
                        onChanged={(box) => this.setState(() => ({
                            category_source: (box.getValueOne() ?? undefined) as CategorySource | undefined,
                            missing_category_names: [],
                        }))}
                    />
                </NSRow>
                <NSRow style={{ justifyContent: 'flex-start' }}>
                    {
                        this.state.category_source === 'Namirasoft' &&
                        <NSBoxCombo
                            ref={this.NSBoxCombo_CategoryID}
                            title="Category"
                            required
                            multiple={false}
                            getOptions={() => this.state.categories.map(category =>
                            {
                                let scope = this.state.category_scopes.find(scope => scope.id === category.category_scope_id);
                                return {
                                    title: scope ? `${scope.name} / ${category.name}` : category.name,
                                    value: category.id,
                                };
                            })}
                        />
                    }
                    {
                        this.state.category_source === 'CSV' &&
                        <NSBoxCombo
                            ref={this.NSBoxCombo_CategoryColumn}
                            title="Category"
                            required
                            multiple={false}
                            getOptions={() => csv_headers}
                            onChanged={() => this.onCategoryColumnChanged()}
                        />
                    }
                    {
                        this.state.category_source === 'CSV' && this.state.missing_category_names.length > 0 &&
                        <NSBoxCombo
                            ref={this.NSBoxCombo_CategoryScopeID}
                            title="Category Scope"
                            required={false}
                            multiple={false}
                            getOptions={() => this.state.category_scopes.map(scope => ({ title: scope.name, value: scope.id }))}
                        />
                    }
                </NSRow>
                {
                    this.state.category_source === 'CSV' && this.state.missing_category_names.length > 0 &&
                    <NSBarAlert
                        title={{
                            title: "",
                            description: `Missing categories: ${this.getMissingCategoryNamesPreview()}. Select a category scope to create them, or leave empty to skip.`,
                        }}
                        button_text=""
                        isVisible={() => true}
                        style={{ maxWidth: NSBox.width.double }}
                        classList={[Styles.ns_bar_alert]}
                    />
                }
                <NSRow style={{ position: 'relative' }}>
                    <NSButtonBlue
                        title="Apply"
                        onClick={{ action: this.createImportedRows }}
                    />
                    <NSButton
                        title=""
                        icon={{ src: 'https://static.namirasoft.com/image/concept/arrow/left/white-blue.svg' }}
                        onClick={{ showLoading: false, action: () => this.props.onBack() }}
                        classList={[ButtonStyles.ns_arrow_button]}
                    />
                </NSRow>
            </NSPanel>
        );
    }
}

export default ImportColumnSettingsStep;